import React, { useMemo } from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartDataPoint, COLOR_PALETTES, Platform } from './chartUtils';

interface GroupedBarChartProps {
  data: ChartDataPoint[];
  title?: string;
}

export const GroupedBarChart: React.FC<GroupedBarChartProps> = ({ data, title }) => {
  // Transform flat array into grouped structure
  const { groupedData, platforms } = useMemo(() => {
    if (data.length === 0) {
      return { groupedData: [], platforms: [] };
    }

    const grouped: Record<string, any> = {};
    const platformSet = new Set<Platform>();

    data.forEach(item => {
      if (!item.platform) return;

      platformSet.add(item.platform);

      if (!grouped[item.label]) {
        grouped[item.label] = { label: item.label };
      }
      grouped[item.label][item.platform] = item.value;
    });

    return {
      groupedData: Object.values(grouped),
      platforms: Array.from(platformSet)
    };
  }, [data]);

  // Empty state
  if (groupedData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-500 dark:text-neutral-400">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  // Calculate dynamic height: 50px per group + 80px for legend/padding
  const height = Math.min(groupedData.length * 50 + 80, 500);

  return (
    <div>
      {title && (
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">
          {title}
        </h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={groupedData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 0, bottom: 5 }}
        >
          <XAxis
            type="number"
            stroke="#9ca3af"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
          />

          <YAxis
            type="category"
            dataKey="label"
            stroke="#9ca3af"
            tick={{ fontSize: 12, fill: '#d1d5db' }}
            width={60}
            tickLine={false}
            axisLine={false}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            labelStyle={{ color: '#f3f4f6' }}
            itemStyle={{ color: '#d1d5db' }}
            cursor={{ fill: 'rgba(156, 163, 175, 0.1)' }}
          />

          <Legend
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
            formatter={(value: string) => (
              <span style={{ color: '#d1d5db', fontSize: '12px' }}>{value}</span>
            )}
          />

          {platforms.map((platform) => (
            <Bar
              key={platform}
              dataKey={platform}
              fill={COLOR_PALETTES.platform[platform]}
              radius={[0, 4, 4, 0]}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
